import React, { useEffect, useRef, useState } from "react";
import { SmilePlus } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * Emoji reactions. The lightest way for a member to respond — most people will never
 * write a comment, but almost everyone will tap an emoji, which is what makes a feed
 * feel inhabited rather than broadcast.
 *
 * The surface shows the reactions people actually gave, each with its count, and one
 * button that opens the picker. It used to render all six options permanently, most
 * of them sitting at zero: that reads as a control panel, it costs a whole row of
 * width on a phone, and a zero tells a reader nothing. What is on the comment now is
 * the response, not the menu.
 *
 * `initial` lets a list hand over counts it already fetched in bulk (comment threads
 * do this). Without it the component fetches its own, which is right for the single
 * row under a post and wrong for thirty rows in a thread.
 *
 * Counts render optimistically and reconcile from the server response, so a tap
 * feels instant on mobile.
 */
export default function Reactions({ targetType, targetId, className = "", initial = null,
                                    size = "md" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(initial);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (initial) { setData(initial); return; }
    api.reactions(targetType, targetId).then((r) => setData(r.data)).catch(() => {});
  }, [targetType, targetId, initial]);

  // Close the picker on an outside tap or Escape. A popover that can only be
  // dismissed by choosing something is a trap, especially on a phone.
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", key); };
  }, [open]);

  const tap = async (emoji) => {
    setOpen(false);
    if (!user) { toast.info(t("reactions.signIn")); return; }
    const prev = data;
    // Optimistic: assume the toggle succeeds, then take the server's word for it.
    setData((d) => {
      if (!d) return d;
      const counts = {};
      (d.types || []).forEach((x) => { counts[x.emoji] = x.count; });
      if (d.mine === emoji) counts[emoji] = Math.max(0, (counts[emoji] || 0) - 1);
      else {
        if (d.mine) counts[d.mine] = Math.max(0, (counts[d.mine] || 0) - 1);
        counts[emoji] = (counts[emoji] || 0) + 1;
      }
      const mine = d.mine === emoji ? null : emoji;
      const types = (d.allowed || [])
        .filter((e) => counts[e] > 0)
        .map((e) => ({ emoji: e, count: counts[e], mine: mine === e }))
        .sort((a, b) => b.count - a.count);
      return { ...d, types, mine, total: types.reduce((s, x) => s + x.count, 0) };
    });
    try {
      const r = await api.react(targetType, targetId, emoji);
      setData(r.data);
    } catch (e) {
      setData(prev);
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    }
  };

  if (!data) return null;
  const types = data.types || [];
  const pad = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm";

  return (
    <div className={`relative flex flex-wrap items-center gap-1.5 ${className}`}
         data-testid={`reactions-${targetId}`}>
      {types.map(({ emoji, count, mine }) => (
        <button
          key={emoji}
          onClick={() => tap(emoji)}
          data-testid={`react-${emoji}`}
          aria-pressed={mine}
          title={t("reactions.countOne", { count })}
          className={`inline-flex items-center gap-1 rounded-full border transition-all active:scale-95 ${pad} ${
            mine ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:bg-slate-50"
          }`}
        >
          <span className="leading-none">{emoji}</span>
          <span className={`text-xs tabular ${mine ? "text-brand-700 font-semibold" : "text-slate-500"}`}>
            {count}
          </span>
        </button>
      ))}

      <div className="relative" ref={popRef}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`react-open-${targetId}`}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={t("reactions.add")}
          title={t("reactions.add")}
          className={`inline-flex items-center rounded-full border border-slate-200 text-slate-400
                      hover:text-brand-600 hover:border-brand-200 hover:bg-slate-50
                      transition-all active:scale-95 ${size === "sm" ? "p-1" : "p-1.5"}`}
        >
          <SmilePlus size={size === "sm" ? 13 : 15} />
        </button>

        {open && (
          <div
            data-testid={`react-picker-${targetId}`}
            role="menu"
            // Anchored to the right edge of the button, not the left: the picker sits
            // at the end of a row of reaction chips, so opening leftwards keeps it on
            // screen. `max-w` and wrapping are the backstop on a narrow phone, where
            // six emoji plus padding can still be wider than what is left of the row.
            className="absolute z-30 bottom-full right-0 mb-2 flex max-w-[calc(100vw-2rem)]
                       flex-wrap items-center justify-end gap-0.5 rounded-xl
                       border border-slate-200 bg-white p-1.5 shadow-lg"
          >
            {(data.allowed || []).map((emoji) => {
              // Once a target carries its maximum number of distinct reactions, the
              // unused ones are shown but inert — with a reason, rather than simply
              // failing when tapped. Joining an existing reaction always works.
              const used = types.some((x) => x.emoji === emoji);
              const blocked = data.full && !used;
              return (
                <button
                  key={emoji}
                  disabled={blocked}
                  onClick={() => tap(emoji)}
                  data-testid={`react-pick-${emoji}`}
                  title={blocked ? t("reactions.capped", { max: data.max_types }) : emoji}
                  className={`rounded-lg px-2 py-1 text-lg leading-none transition-transform ${
                    blocked ? "opacity-25 cursor-not-allowed"
                            : "hover:bg-slate-100 hover:scale-110 active:scale-95"
                  } ${data.mine === emoji ? "bg-brand-50" : ""}`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
